import { createClient } from '@supabase/supabase-js';

// Environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

// Only log configuration details in development mode
if (process.env.NODE_ENV === 'development') {
  console.log('Supabase configuration:', {
    urlConfigured: !!supabaseUrl,
    keyConfigured: !!supabaseAnonKey
  });
}

// Create Supabase client with global timeout configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: (url, options = {}) => {
      // Auth operations (signup, token, etc.) need longer timeout for SMTP email delivery
      const isAuthRequest = typeof url === 'string' && url.includes('/auth/v1/');
      const timeoutMs = isAuthRequest ? 30000 : 10000;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      // Merge headers to prevent browser caching of Supabase requests
      const headers = new Headers(options.headers || {});
      headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
      headers.set('Pragma', 'no-cache');

      return fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
        cache: 'no-store' // Force fresh fetch, bypass HTTP cache
      }).finally(() => clearTimeout(timeoutId));
    }
  }
});

// Cache management
const cache = new Map();
const cacheTimeout = 5 * 60 * 1000; // 5 minutes

const getCachedRequest = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < cacheTimeout) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Returning cached data for:', key);
    }
    return cached.data;
  }
  return null;
};

const setCachedRequest = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Clear cache - either a specific entry or all entries
 * @param {string} [key] - The cache key to clear. If omitted, clears all cache entries.
 */
export const clearCache = (key) => {
  if (key) {
    cache.delete(key);
    if (process.env.NODE_ENV === 'development') {
      console.log('Cache cleared for:', key);
    }
  } else {
    cache.clear();
    if (process.env.NODE_ENV === 'development') {
      console.log('All cache cleared');
    }
  }
};

/**
 * Clear the ship hierarchy cache specifically
 * Use this when ships are added, edited, or deleted
 */
export const clearShipCache = () => {
  clearCache('shipHierarchy');
};

// Enhanced getCommodities with comprehensive error handling
export const getCommodities = async () => {
  const cacheKey = 'commodities';
  const cached = getCachedRequest(cacheKey);

  if (cached) {
    console.log('Returning cached commodities data');
    return cached;
  }

  console.log('Attempting to fetch commodities from Supabase (items table)...');

  try {
    // Query from items table where category='Commodities' (unified commodity system)
    const { data, error } = await supabase
      .from('items')
      .select('*')
      .eq('category', 'Commodities')
      .order('name')
      .limit(1000);

    if (error) {
      console.error('Supabase error in getCommodities:', error);
      throw error;
    }

    console.log('Commodities fetched successfully from items table, count:', data?.length || 0);
    setCachedRequest(cacheKey, data);
    return data;
  } catch (error) {
    console.error('Get commodities error:', error);
    
    const expiredCache = cache.get(cacheKey);
    if (expiredCache) {
      console.log('Returning expired cached commodities data as fallback');
      return expiredCache.data;
    }
    
    console.log('Returning empty array as fallback for commodities');
    return [];
  }
};

// Enhanced getMerchants with comprehensive error handling
export const getMerchants = async () => {
  const cacheKey = 'merchants';
  const cached = getCachedRequest(cacheKey);

  if (cached) {
    console.log('Returning cached merchants data');
    return cached;
  }

  console.log('Attempting to fetch merchants from Supabase...');

  try {
    const { data, error } = await supabase
      .from('merchants')
      .select('*')
      .order('name')
      .limit(2000);

    if (error) {
      console.error('Supabase error in getMerchants:', error);
      throw error;
    }

    console.log('Merchants fetched successfully, count:', data?.length || 0);
    setCachedRequest(cacheKey, data);
    return data;
  } catch (error) {
    console.error('Get merchants error:', error);
    
    const expiredCache = cache.get(cacheKey);
    if (expiredCache) {
      console.log('Returning expired cached merchants data as fallback');
      return expiredCache.data;
    }
    
    console.log('Returning empty array as fallback for merchants');
    return [];
  }
};

// Merchant commodities functions
export const getMerchantCommodities = async () => {
  try {
    console.log('Attempting to fetch merchant commodities from Supabase...');

    const { data, error } = await supabase
        .from('merchant_commodities')
        .select(`
          id,
          merchant_id,
          commodity_id,
          activity,
          accepted_scus,
          price_history,
          last_buy_price,
          last_sell_price,
          last_inventory_status
        `)
        .order('merchant_id')
        .limit(5000);

    if (error) {
      console.error('Get merchant commodities error:', error);
      throw error;
    }

    console.log('Merchant commodities fetched successfully, count:', data?.length || 0);
    return data;
  } catch (error) {
    console.error('Get merchant commodities error:', error);
    return [];
  }
};

// Ships functions
export const getShips = async () => {
  const timeoutMs = 8000;
  const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('getShips timeout')), timeoutMs));
  try {
    console.log('Attempting to fetch ships from Supabase...');

    const supabasePromise = (async () => {
      // Only fetch depth 1+ ships (those with parent_ship_id)
      // Depth 0 ships are chassis groupings, not selectable ships
      const { data, error } = await supabase
        .from('ships')
        .select('*')
        .eq('is_archived', false)
        .not('parent_ship_id', 'is', null)
        .order('name')
        .limit(1000);

      if (error) {
        console.error('Get ships error:', error);
        throw error;
      }

      console.log('Ships fetched successfully, count:', data?.length || 0);
      return data;
    })();

    return await Promise.race([supabasePromise, timeoutPromise]);
  } catch (error) {
    console.error('Get ships error:', error);
    // Fallback to API route if available
    try {
      const apiData = await getShipsFromApi();
      if (Array.isArray(apiData) && apiData.length >= 0) {
        console.log('Using API fallback for ships, count:', apiData.length);
        return apiData;
      }
    } catch (e) {
      console.error('API fallback for ships failed:', e);
    }
    return [];
  }
};

export const getShipsFromApi = async () => {
  try {
    const res = await fetch('/api/features/ships-matrix', {
      headers: await (async () => {
        try {
          const { data } = await supabase.auth.getSession();
          const token = data?.session?.access_token;
          return token ? { Authorization: `Bearer ${token}` } : {};
        } catch { return {}; }
      })()
    });
    if (!res.ok) throw new Error('Failed to fetch ships');
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) throw new Error('Invalid response');
    return await res.json();
  } catch (_) {
    return [];
  }
};

// Get cache status
export const getCacheStatus = () => {
  return {
    cacheSize: cache.size
  };
};

// Test database connectivity
export const testDatabaseConnectivity = async () => {
  try {
    const { error } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true });
    
    if (error) throw error;
    
    console.log('Supabase connection test successful');
    return true;
  } catch (error) {
    console.error('Supabase connection test failed:', error);
    return false;
  }
};

// Newsletter functions
export const subscribeToNewsletter = async (email) => {
  try {
    console.log('Attempting to subscribe email:', email);
    
    const { data, error } = await supabase
      .from('newsletters')
      .insert([{ email }])
      .select();

    if (error) {
      console.error('Supabase error details:', error);
      if (error.code === '23505') { // Unique constraint violation
        return { success: false, message: 'This email is already subscribed to our newsletter.' };
      }
      throw error;
    }

    console.log('Newsletter subscription successful:', data);
    return { success: true, message: 'Successfully subscribed to newsletter!' };
  } catch (error) {
    console.error('Newsletter subscription error:', error);
    return { success: false, message: 'Failed to subscribe. Please try again.' };
  }
};

// Profile newsletter opt-in functions
export const updateNewsletterOptIn = async (userId, optIn) => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ newsletter_opt_in: optIn })
      .eq('id', userId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Update newsletter opt-in error:', error);
    return { success: false, message: 'Failed to update newsletter preference.' };
  }
};

export const getNewsletterOptInStatus = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('newsletter_opt_in')
      .eq('id', userId)
      .single();

    if (error) throw error;

    return { success: true, optIn: data.newsletter_opt_in };
  } catch (error) {
    console.error('Get newsletter opt-in error:', error);
    return { success: false, optIn: false };
  }
}; 

// Commodities functions
export const createCommodity = async (commodity) => {
  try {
    const { data, error } = await supabase
      .from('commodities_new')
      .insert([commodity])
      .select()
      .single();

    if (error) throw error;
    clearCache(); // Invalidate cache
    return { success: true, data };
  } catch (error) {
    console.error('Create commodity error:', error);
    return { success: false, message: error.message };
  }
};

export const updateCommodity = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('commodities_new')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    clearCache(); // Invalidate cache
    return { success: true, data };
  } catch (error) {
    console.error('Update commodity error:', error);
    return { success: false, message: error.message };
  }
};

export const deleteCommodity = async (id) => {
  try {
    const { error } = await supabase
      .from('commodities_new')
      .delete()
      .eq('id', id);

    if (error) {
        console.error('Delete commodity error:', error);
        if (error.message.includes('permission denied')) {
             return { success: false, message: "Permission denied. You may not have the rights to delete this commodity." };
        }
        return { success: false, message: error.message };
    }
    clearCache(); // Invalidate cache
    return { success: true };
  } catch (error) {
    console.error('Caught exception in deleteCommodity:', error);
    return { success: false, message: error.message };
  }
};

// Merchants functions
export const createMerchant = async (merchant) => {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .insert([merchant])
      .select()
      .single();

    if (error) throw error;
    clearCache(); // Invalidate cache
    return { success: true, data };
  } catch (error) {
    console.error('Create merchant error:', error);
    return { success: false, message: error.message };
  }
};

export const updateMerchant = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('merchants')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    clearCache(); // Invalidate cache
    return { success: true, data };
  } catch (error) {
    console.error('Update merchant error:', error);
    return { success: false, message: error.message };
  }
};

export const deleteMerchant = async (id) => {
  try {
    const { error } = await supabase
      .from('merchants')
      .delete()
      .eq('id', id);

    if (error) throw error;
    clearCache(); // Invalidate cache
    return { success: true };
  } catch (error) {
    console.error('Delete merchant error:', error);
    return { success: false, message: error.message };
  }
}; 

// Utility: wrap a promise with a timeout to avoid UI hangs
const withTimeout = async (promise, ms, label = 'operation') => {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timeout`)), ms);
      })
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

// Ships functions
export const createShip = async (ship) => {
  try {
    console.log('Attempting to create ship:', ship);

    // Pre-check: if a ship with same manufacturer+name(+variant) exists, update instead
    try {
      if (ship?.name && ship?.manufacturer) {
        let q = supabase
          .from('ships')
          .select('id, name, variant_name')
          .eq('name', ship.name)
          .eq('manufacturer', ship.manufacturer)
          .limit(1);
        if (ship.parent_ship_id) {
          q = q.eq('parent_ship_id', ship.parent_ship_id);
          if (ship.variant_name) q = q.eq('variant_name', ship.variant_name);
        } else {
          q = q.is('parent_ship_id', null);
        }
        const { data: existsRows, error: existsErr } = await withTimeout(q, 8000, 'createShip precheck');
        if (!existsErr && Array.isArray(existsRows) && existsRows.length > 0) {
          const existingShip = existsRows[0];
          const existingId = existingShip?.id;
          if (existingId) {
            console.log('Existing ship found by natural key; switching to update:', existingId);
            const { error: updErr } = await withTimeout(
              supabase
                .from('ships')
                .update(ship, { returning: 'minimal' })
                .eq('id', existingId),
              15000,
              'createShip->updateExisting'
            );
            if (updErr) {
              console.error('Update existing ship failed:', updErr);
              return {
                success: false,
                error: updErr.message,
                details: `Failed to update existing ship "${existingShip.name}${existingShip.variant_name ? ' - ' + existingShip.variant_name : ''}". ${updErr.hint || ''}`
              };
            }
            clearCache();
            // Return success with info that we updated instead of created
            return {
              success: true,
              wasUpdate: true,
              message: `Ship "${existingShip.name}${existingShip.variant_name ? ' - ' + existingShip.variant_name : ''}" already exists - updated instead.`,
              existingId
            };
          }
        }
      }
    } catch (e) {
      console.warn('createShip precheck warning (continuing to insert):', e?.message || e);
    }

    // Use .select('id') to return the created ID - fixes race condition where
    // caller had to do a separate query to get the ID
    const { data: insertedData, error } = await withTimeout(
      supabase
        .from('ships')
        .insert([ship])
        .select('id'),
      15000,
      'createShip'
    );

    if (error) {
      console.error('Supabase error in createShip:', {
        message: error?.message,
        details: error?.details,
        hint: error?.hint
      });
      // Fallback: if missing optional columns, retry without them
      const message = String(error.message || '').toLowerCase();
      const details = String(error.details || '').toLowerCase();
      const hint = String(error.hint || '').toLowerCase();
      const combined = `${message} ${details} ${hint}`;

      // Retry without notes
      if (combined.includes('notes') && (combined.includes('could not find') || combined.includes('column'))) {
        const { notes, ...withoutNotes } = ship || {};
        console.log('Retrying createShip without notes field due to missing column');
        const { data: retryData, error: error2 } = await withTimeout(
          supabase.from('ships').insert([withoutNotes]).select('id'),
          15000,
          'createShip (retry without notes)'
        );
        if (!error2) {
          clearCache();
          const createdId = retryData?.[0]?.id;
          return { success: true, id: createdId, strippedFields: ['notes'] };
        }
      }

      // Retry without cig_store_url if missing
      if (combined.includes('cig_store_url') && (combined.includes('could not find') || combined.includes('column'))) {
        const { cig_store_url, ...withoutCig } = ship || {};
        console.log('Retrying createShip without cig_store_url due to missing column');
        const { data: retryData, error: error2 } = await withTimeout(
          supabase.from('ships').insert([withoutCig]).select('id'),
          15000,
          'createShip (retry without cig_store_url)'
        );
        if (!error2) {
          clearCache();
          const createdId = retryData?.[0]?.id;
          return { success: true, id: createdId, strippedFields: ['cig_store_url'] };
        }
      }

      // Retry without *_locations_ext if missing
      if ((combined.includes('purchase_locations_ext') || combined.includes('rental_locations_ext')) && (combined.includes('could not find') || combined.includes('column'))) {
        const { purchase_locations_ext, rental_locations_ext, ...withoutExt } = ship || {};
        console.log('Retrying createShip without *_locations_ext due to missing columns');
        const { data: retryData, error: error2 } = await withTimeout(
          supabase.from('ships').insert([withoutExt]).select('id'),
          15000,
          'createShip (retry without *_locations_ext)'
        );
        if (!error2) {
          clearCache();
          const createdId = retryData?.[0]?.id;
          return { success: true, id: createdId, strippedFields: ['purchase_locations_ext', 'rental_locations_ext'] };
        }
      }

      // Retry without rental_price if missing
      if (combined.includes('rental_price') && (combined.includes('could not find') || combined.includes('column'))) {
        const { rental_price, ...withoutRentalPrice } = ship || {};
        console.log('Retrying createShip without rental_price due to missing column');
        const { data: retryData, error: error2 } = await withTimeout(
          supabase.from('ships').insert([withoutRentalPrice]).select('id'),
          15000,
          'createShip (retry without rental_price)'
        );
        if (!error2) {
          clearCache();
          const createdId = retryData?.[0]?.id;
          return { success: true, id: createdId, strippedFields: ['rental_price'] };
        }
      }

      // Final fallback: insert with minimal required fields only
      try {
        const minimal = {
          manufacturer: ship?.manufacturer,
          name: ship?.name,
          category: ship?.category,
          sub_category: ship?.sub_category,
          parent_ship_id: ship?.parent_ship_id ?? null,
          variant_name: ship?.variant_name ?? null
        };
        console.log('Retrying createShip with minimal payload');
        const { data: minData, error: errorMin } = await withTimeout(
          supabase.from('ships').insert([minimal]).select('id'),
          15000,
          'createShip (retry minimal)'
        );
        if (!errorMin) {
          clearCache();
          const createdId = minData?.[0]?.id;
          return { success: true, id: createdId, strippedFields: ['minimal_fallback'] };
        }
        console.error('Minimal payload insert failed:', { message: errorMin?.message, details: errorMin?.details, hint: errorMin?.hint });
      } catch (ignore) {}

      // If we got here, rethrow for outer catch
      throw error;
    }

    clearCache();
    // Return the created ID to avoid race condition where caller had to query separately
    const createdId = insertedData?.[0]?.id;
    return { success: true, id: createdId };
  } catch (error) {
    console.error('Create ship error:', { message: error?.message, details: error?.details, hint: error?.hint, code: error?.code });

    // Build detailed error message for UI
    const msg = String(error.message || '').toLowerCase();
    let detailedError = error.message || 'Unknown error';

    // Check for RLS policy violation (permission denied)
    if (msg.includes('row-level security') || msg.includes('rls') || msg.includes('policy') || error.code === '42501') {
      detailedError = 'Permission denied: You may not have the required role (moderator/admin/owner) to create ships. Please check your login status.';
    }
    // Check for duplicate key
    else if (msg.includes('duplicate') || msg.includes('unique') || error.code === '23505') {
      detailedError = `A ship with this name/manufacturer combination already exists. Ship: "${ship?.name}" by ${ship?.manufacturer}`;
    }
    // Check for foreign key violation
    else if (msg.includes('foreign key') || error.code === '23503') {
      detailedError = `Invalid reference: The parent ship ID "${ship?.parent_ship_id}" does not exist.`;
    }

    // If timeout, try to detect row existence by natural key and proceed
    try {
      if (msg.includes('timeout') && ship?.name && ship?.manufacturer) {
        let query = supabase
          .from('ships')
          .select('id', { count: 'exact', head: true })
          .eq('name', ship.name)
          .eq('manufacturer', ship.manufacturer);
        if (ship.parent_ship_id) {
          query = query.eq('parent_ship_id', ship.parent_ship_id);
          if (ship.variant_name) query = query.eq('variant_name', ship.variant_name);
        } else {
          query = query.is('parent_ship_id', null);
        }
        const { count, error: checkError } = await withTimeout(query, 15000, 'createShip existence check');
        if (!checkError && typeof count === 'number' && count > 0) {
          console.log('Insert likely succeeded despite timeout; proceeding as success');
          clearCache();
          return { success: true, message: 'Ship created (verified after timeout)' };
        }
        // Return warning instead of optimistic success
        console.warn('Insert timeout and verification failed - returning error');
        return {
          success: false,
          error: 'Operation timed out and could not verify if ship was created. Please refresh and check.',
          details: `Attempted to create: "${ship?.name}" by ${ship?.manufacturer}`
        };
      }
    } catch (ignore) {}

    return {
      success: false,
      error: detailedError,
      details: error.hint || error.details || null,
      code: error.code
    };
  }
};

export const updateShip = async (id, updates) => {
  try {
    console.log('Attempting to update ship:', id, updates);
    const { error } = await withTimeout(
      supabase
        .from('ships')
        .update(updates, { returning: 'minimal' })
        .eq('id', id),
      15000,
      'updateShip'
    );

    if (error) {
      console.error('Supabase error in updateShip:', error);
      // Fallbacks: remove optional/missing columns and retry
      const message = String(error.message || '').toLowerCase();
      const details = String(error.details || '').toLowerCase();
      const hint = String(error.hint || '').toLowerCase();
      const combined = `${message} ${details} ${hint}`;

      // Retry without notes
      if (combined.includes('notes') && (combined.includes('could not find') || combined.includes('column'))) {
        const { notes, ...withoutNotes } = updates || {};
        console.log('Retrying updateShip without notes field due to missing column');
        const { error: error2 } = await withTimeout(
          supabase
            .from('ships')
            .update(withoutNotes, { returning: 'minimal' })
            .eq('id', id),
          15000,
          'updateShip (retry without notes)'
        );
        if (!error2) { clearCache(); return { success: true }; }
      }

      // Retry without cig_store_url if missing
      if (combined.includes('cig_store_url') && (combined.includes('could not find') || combined.includes('column'))) {
        const { cig_store_url, ...withoutCig } = updates || {};
        console.log('Retrying updateShip without cig_store_url due to missing column');
        const { error: error2 } = await withTimeout(
          supabase
            .from('ships')
            .update(withoutCig, { returning: 'minimal' })
            .eq('id', id),
          15000,
          'updateShip (retry without cig_store_url)'
        );
        if (!error2) { clearCache(); return { success: true }; }
      }

      // Retry without *_locations_ext if missing
      if ((combined.includes('purchase_locations_ext') || combined.includes('rental_locations_ext')) && (combined.includes('could not find') || combined.includes('column'))) {
        const { purchase_locations_ext, rental_locations_ext, ...withoutExt } = updates || {};
        console.log('Retrying updateShip without *_locations_ext due to missing columns');
        const { error: error2 } = await withTimeout(
          supabase
            .from('ships')
            .update(withoutExt, { returning: 'minimal' })
            .eq('id', id),
          15000,
          'updateShip (retry without *_locations_ext)'
        );
        if (!error2) { clearCache(); return { success: true }; }
      }

      throw error;
    }

    console.log('Ship updated successfully');
    clearCache();
    return { success: true };
  } catch (error) {
    console.error('Update ship error:', { message: error?.message, details: error?.details, hint: error?.hint, code: error?.code });

    // Build detailed error message for UI
    const msg = String(error.message || '').toLowerCase();
    let detailedError = error.message || 'Unknown error';

    // Check for RLS policy violation (permission denied)
    if (msg.includes('row-level security') || msg.includes('rls') || msg.includes('policy') || error.code === '42501') {
      detailedError = 'Permission denied: You may not have the required role (moderator/admin/owner) to update ships. Please check your login status.';
    }
    // Check for not found
    else if (msg.includes('no rows') || error.code === 'PGRST116') {
      detailedError = `Ship with ID "${id}" not found or you don't have permission to update it.`;
    }

    // If we timed out, verify the row still exists
    try {
      if (msg.includes('timeout') && id) {
        const { error: fetchErr } = await withTimeout(
          supabase
            .from('ships')
            .select('id', { head: true, count: 'exact' })
            .eq('id', id),
          15000,
          'updateShip existence check'
        );
        if (!fetchErr) {
          console.log('Update likely applied despite timeout; proceeding as success');
          clearCache();
          return { success: true, message: 'Ship updated (verified after timeout)' };
        }
        return {
          success: false,
          error: 'Update timed out and could not verify. Please refresh and check.',
          details: `Ship ID: ${id}`
        };
      }
    } catch (ignore) {}

    return {
      success: false,
      error: detailedError,
      details: error.hint || error.details || null,
      code: error.code
    };
  }
};

export const deleteShip = async (id) => {
  try {
    console.log('Attempting to delete ship:', id);
    const { error } = await withTimeout(
      supabase
        .from('ships')
        .delete()
        .eq('id', id),
      15000,
      'deleteShip'
    );

    if (error) {
      console.error('Supabase error in deleteShip:', error);
      throw error;
    }

    console.log('Ship deleted successfully');
    clearCache(); // Invalidate cache
    return { success: true };
  } catch (error) {
    console.error('Delete ship error:', error);
    return { success: false, error: error.message };
  }
};

// Upload image to Supabase Storage (bucket: wikelo)
export const uploadWikeloImage = async (file) => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
    const storagePath = `quests/${fileName}`;
    const { error } = await supabase.storage.from('wikelo').upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    });
    if (error) throw error;
    const { data: publicUrl } = supabase.storage.from('wikelo').getPublicUrl(storagePath);
    return { success: true, url: publicUrl.publicUrl };
  } catch (error) {
    console.error('Upload wikelo image error:', error);
    return { success: false, message: error.message };
  }
};

// Wikelo quests CRUD
export const getWikeloQuests = async (shipId) => {
  try {
    const query = supabase.from('wikelo_quests').select('*');
    const { data, error } = shipId ? await query.eq('ship_id', shipId) : await query;
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Get wikelo quests error:', error);
    return { success: false, message: error.message };
  }
};

export const createWikeloQuest = async (quest) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_quests')
      .insert([quest])
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Create wikelo quest error:', error);
    return { success: false, message: error.message };
  }
};

export const updateWikeloQuest = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_quests')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Update wikelo quest error:', error);
    return { success: false, message: error.message };
  }
};

export const deleteWikeloQuest = async (id) => {
  try {
    const { error } = await supabase
      .from('wikelo_quests')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Delete wikelo quest error:', error);
    return { success: false, message: error.message };
  }
};

// Wikelo mappings functions
export const getWikeloMappings = async () => {
  try {
    const { data, error } = await supabase
      .from('wikelo_mappings')
      .select('*')
      .order('manufacturer')
      .limit(2000);
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Get wikelo mappings error:', error);
    return { success: false, message: error.message };
  }
};

export const upsertWikeloMapping = async (mapping) => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const payload = userId ? { ...mapping, created_by: mapping.created_by || userId } : mapping;
    const { data, error } = await supabase
      .from('wikelo_mappings')
      .upsert(payload, { onConflict: 'manufacturer,ship_name' })
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Upsert wikelo mapping error:', error);
    return { success: false, message: error.message };
  }
};

export const deleteWikeloMapping = async (id) => {
  try {
    const { error } = await supabase
      .from('wikelo_mappings')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Delete wikelo mapping error:', error);
    return { success: false, message: error.message };
  }
};

// Add helper to delete mapping by composite key
export const removeWikeloMappingFor = async (manufacturer, shipName) => {
  try {
    const { error } = await supabase
      .from('wikelo_mappings')
      .delete()
      .eq('manufacturer', manufacturer)
      .eq('ship_name', shipName);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Remove wikelo mapping by key error:', error);
    return { success: false, message: error.message };
  }
};

// Wikelo content functions
export const getWikeloPageContent = async () => {
  try {
    const { data, error } = await supabase
      .from('wikelo_page_content')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Get wikelo page content error:', error);
    // Gracefully handle missing table on new environments
    if (String(error.message || '').includes('404') || String(error.code || '') === '404') {
      return { success: true, data: null };
    }
    return { success: false, message: error.message };
  }
};

export const upsertWikeloPageContent = async (content) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_page_content')
      .upsert(content)
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Upsert wikelo page content error:', error);
    if (String(error.message || '').includes('404') || String(error.code || '') === '404') {
      return { success: false, message: 'Wikelo content tables not found. Please run the provided migrations in Supabase.' };
    }
    return { success: false, message: error.message };
  }
};

// Wikelo rewards and requirements
export const listWikeloRewards = async (filters = {}) => {
  try {
    let query = supabase.from('wikelo_rewards').select('*').eq('active', true);
    if (filters.kind) query = query.eq('kind', filters.kind);
    if (filters.ship_id) query = query.eq('ship_id', filters.ship_id);
    if (filters.subtype) query = query.eq('subtype', filters.subtype);
    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('List wikelo rewards error:', error);
    if (String(error.message || '').includes('404') || String(error.code || '') === '404') {
      return { success: true, data: [] };
    }
    return { success: false, message: error.message };
  }
};

export const createWikeloReward = async (reward) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_rewards')
      .insert([reward])
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Create wikelo reward error:', error);
    return { success: false, message: error.message };
  }
};

export const updateWikeloReward = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_rewards')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Update wikelo reward error:', error);
    return { success: false, message: error.message };
  }
};

export const deleteWikeloReward = async (id) => {
  try {
    const { error } = await supabase
      .from('wikelo_rewards')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Delete wikelo reward error:', error);
    return { success: false, message: error.message };
  }
};

// Wikelo ignores functions
export const getWikeloIgnores = async () => {
  try {
    const { data, error } = await supabase
      .from('wikelo_ignores')
      .select('*')
      .order('manufacturer')
      .limit(2000);
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    // If table is missing on some environments, treat as empty set rather than hard error
    const code = String(error?.code || '');
    if (code === '42P01') {
      return { success: true, data: [] };
    }
    console.error('Get wikelo ignores error:', error);
    return { success: false, message: error.message };
  }
};

export const upsertWikeloIgnore = async (ignore) => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const payload = userId ? { ...ignore, created_by: ignore.created_by || userId } : ignore;
    const { data, error } = await supabase
      .from('wikelo_ignores')
      .upsert(payload, { onConflict: 'manufacturer,ship_name' })
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    // Gracefully no-op if the table is missing
    const code = String(error?.code || '');
    if (code === '42P01') {
      return { success: true, data: null };
    }
    console.error('Upsert wikelo ignore error:', error);
    return { success: false, message: error.message };
  }
};

export const removeWikeloIgnoreFor = async (manufacturer, shipName) => {
  try {
    const { error } = await supabase
      .from('wikelo_ignores')
      .delete()
      .eq('manufacturer', manufacturer)
      .eq('ship_name', shipName);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    // Gracefully ignore if table is missing
    const code = String(error?.code || '');
    if (code === '42P01') {
      return { success: true };
    }
    console.error('Remove wikelo ignore by key error:', error);
    return { success: false, message: error.message };
  }
};

export const listWikeloRewardRequirements = async (rewardId) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_reward_requirements')
      .select('*')
      .eq('reward_id', rewardId)
      .order('created_at');
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('List wikelo reward requirements error:', error);
    return { success: false, message: error.message };
  }
};

export const addWikeloRewardRequirement = async (rewardId, requirement) => {
  try {
    const payload = { ...requirement, reward_id: rewardId };
    const { data, error } = await supabase
      .from('wikelo_reward_requirements')
      .insert([payload])
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Add wikelo reward requirement error:', error);
    return { success: false, message: error.message };
  }
};

export const updateWikeloRewardRequirement = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_reward_requirements')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Update wikelo reward requirement error:', error);
    return { success: false, message: error.message };
  }
};

export const deleteWikeloRewardRequirement = async (id) => {
  try {
    const { error } = await supabase
      .from('wikelo_reward_requirements')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Delete wikelo reward requirement error:', error);
    return { success: false, message: error.message };
  }
};

// Wiki pages
export const listWikiPages = async () => {
  try {
    const { data, error } = await supabase
      .from('wiki_pages')
      .select('id, slug, title, page_type, created_at, updated_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('List wiki pages error:', error);
    return { success: false, message: error.message };
  }
};

export const getWikiPageBySlug = async (slug) => {
  try {
    const { data, error } = await supabase
      .from('wiki_pages')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Get wiki page error:', error);
    return { success: false, message: error.message };
  }
};

export const createWikiPage = async (page) => {
  try {
    const { data, error } = await supabase
      .from('wiki_pages')
      .insert([page])
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Create wiki page error:', error);
    return { success: false, message: error.message };
  }
};

export const updateWikiPage = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('wiki_pages')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Update wiki page error:', error);
    return { success: false, message: error.message };
  }
};

export const deleteWikiPage = async (id) => {
  try {
    const { error } = await supabase
      .from('wiki_pages')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Delete wiki page error:', error);
    return { success: false, message: error.message };
  }
};

export const listWikiTemplates = async () => {
  try {
    const { data, error } = await supabase
      .from('wiki_templates')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('List wiki templates error:', error);
    return { success: false, message: error.message };
  }
};

export const getWikiTemplateBySlug = async (slug) => {
  try {
    const { data, error } = await supabase
      .from('wiki_templates')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Get wiki template error:', error);
    return { success: false, message: error.message };
  }
};

export const createWikiTemplate = async (tpl) => {
  try {
    const { data, error } = await supabase
      .from('wiki_templates')
      .insert([tpl])
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Create wiki template error:', error);
    return { success: false, message: error.message };
  }
};

// Executive mappings functions
export const getExecutiveMappings = async () => {
  try {
    const { data, error } = await supabase
      .from('executive_mappings')
      .select('*')
      .order('manufacturer')
      .limit(2000);
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Get executive mappings error:', error);
    // Gracefully handle missing table
    const code = String(error?.code || '');
    if (code === '42P01') {
      return { success: true, data: [] };
    }
    return { success: false, message: error.message };
  }
};

export const upsertExecutiveMapping = async (mapping) => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const payload = userId ? { ...mapping, created_by: mapping.created_by || userId } : mapping;
    const { data, error } = await supabase
      .from('executive_mappings')
      .upsert(payload, { onConflict: 'manufacturer,ship_name' })
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Upsert executive mapping error:', error);
    return { success: false, message: error.message };
  }
};

export const removeExecutiveMappingFor = async (manufacturer, shipName) => {
  try {
    const { error } = await supabase
      .from('executive_mappings')
      .delete()
      .eq('manufacturer', manufacturer)
      .eq('ship_name', shipName);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Remove executive mapping by key error:', error);
    return { success: false, message: error.message };
  }
};

// Executive ignores functions
export const getExecutiveIgnores = async () => {
  try {
    const { data, error } = await supabase
      .from('executive_ignores')
      .select('*')
      .order('manufacturer')
      .limit(2000);
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    const code = String(error?.code || '');
    if (code === '42P01') {
      return { success: true, data: [] };
    }
    console.error('Get executive ignores error:', error);
    return { success: false, message: error.message };
  }
};

export const upsertExecutiveIgnore = async (ignore) => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const payload = userId ? { ...ignore, created_by: ignore.created_by || userId } : ignore;
    const { data, error } = await supabase
      .from('executive_ignores')
      .upsert(payload, { onConflict: 'manufacturer,ship_name' })
      .select()
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    const code = String(error?.code || '');
    if (code === '42P01') {
      return { success: true, data: null };
    }
    console.error('Upsert executive ignore error:', error);
    return { success: false, message: error.message };
  }
};

export const removeExecutiveIgnoreFor = async (manufacturer, shipName) => {
  try {
    const { error } = await supabase
      .from('executive_ignores')
      .delete()
      .eq('manufacturer', manufacturer)
      .eq('ship_name', shipName);
    if (error) throw error;
    return { success: true };
  } catch (error) {
    const code = String(error?.code || '');
    if (code === '42P01') {
      return { success: true };
    }
    console.error('Remove executive ignore by key error:', error);
    return { success: false, message: error.message };
  }
};

// Wikelo Items and Inventory APIs
export const searchWikeloItems = async (q, includeUnapproved = false) => {
  try {
    const { data, error } = await supabase.rpc('search_wikelo_items', { q, include_unapproved: includeUnapproved });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const listBaseItemsWithVariants = async (onlyApproved = true) => {
  try {
    let baseQuery = supabase.from('wikelo_items').select('*').is('parent_item_id', null);
    if (onlyApproved) baseQuery = baseQuery.eq('approved', true);
    const { data: bases, error } = await baseQuery.order('name');
    if (error) throw error;
    if (!Array.isArray(bases) || bases.length === 0) return { success: true, data: [] };
    const baseIds = bases.map(b => b.id);
    let varQuery = supabase.from('wikelo_items').select('*').in('parent_item_id', baseIds);
    if (onlyApproved) varQuery = varQuery.eq('approved', true);
    const { data: variants, error: vErr } = await varQuery;
    if (vErr) throw vErr;
    const byParent = new Map();
    for (const v of variants || []) {
      const arr = byParent.get(v.parent_item_id) || [];
      arr.push(v);
      byParent.set(v.parent_item_id, arr);
    }
    const rows = bases.map(b => ({ base: b, variants: byParent.get(b.id) || [] }));
    return { success: true, data: rows };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const submitWikeloItem = async (payload) => {
  try {
    const body = { ...payload, approved: false };
    const { data, error } = await supabase.from('wikelo_items').insert([body]).select('*').single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const approveWikeloItem = async (id, updates = {}) => {
  try {
    const { data, error } = await supabase
      .from('wikelo_items')
      .update({ ...updates, approved: true })
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const mergeWikeloItems = async (sourceId, targetId) => {
  try {
    const res = await fetch('/functions/v1/merge-wikelo-items', {
      method: 'POST',
      headers: await (async () => {
        try { const { data } = await supabase.auth.getSession(); const t = data?.session?.access_token; return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; } catch { return { 'Content-Type': 'application/json' }; }
      })(),
      body: JSON.stringify({ source_item_id: sourceId, target_item_id: targetId }),
    });
    if (!res.ok) throw new Error(await res.text());
    return { success: true };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const addItemToInventory = async (itemId, quantity = 1) => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) throw new Error('Not authenticated');
    const { error } = await supabase.rpc('add_to_wikelo_inventory', { p_user_id: userId, p_item_id: itemId, p_quantity: quantity });
    if (error) throw error;
    await supabase.rpc('refresh_item_goals_for_user', { p_user_id: userId }).catch(() => {});
    return { success: true };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const listMyWikeloInventory = async () => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    const { data, error } = await supabase
      .from('player_wikelo_inventory')
      .select('id, item_id, quantity, added_at, wikelo_items!inner(id, name, parent_item_id, variant_name, category, image_url)')
      .eq('user_id', userId)
      .order('added_at', { ascending: false });
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const createItemGoal = async ({ base_item_id, variant_item_id, target_quantity }) => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    const { data, error } = await supabase
      .from('item_goals')
      .insert([{ user_id: userId, base_item_id: base_item_id || null, variant_item_id: variant_item_id || null, target_quantity: target_quantity || 1 }])
      .select('*')
      .single();
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const listItemGoals = async () => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    const { data, error } = await supabase
      .from('item_goals')
      .select('*')
      .eq('user_id', userId)
      .order('created_at');
    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

export const listBaseShipsWithVariants = async () => {
  try {
    const { data: bases, error } = await supabase.from('ships').select('*').eq('is_archived', false).is('parent_ship_id', null).order('manufacturer').order('name');
    if (error) throw error;
    if (!Array.isArray(bases) || bases.length === 0) return { success: true, data: [] };
    const ids = bases.map(b => b.id);
    const { data: variants, error: e2 } = await supabase.from('ships').select('*').eq('is_archived', false).in('parent_ship_id', ids).order('variant_name');
    if (e2) throw e2;
    const variantIds = (variants || []).map(v => v.id);
    let childrenByParent = new Map();
    if (variantIds.length > 0) {
      const { data: children, error: e3 } = await supabase.from('ships').select('*').eq('is_archived', false).in('parent_ship_id', variantIds).order('variant_name');
      if (e3) throw e3;
      (children || []).forEach(ch => {
        const arr = childrenByParent.get(ch.parent_ship_id) || [];
        // Mark as edition (depth 2) - this is the SOURCE OF TRUTH for edition status
        // Never rely on name keywords to determine if something is an edition
        arr.push({ ...ch, _isEdition: true, _depth: 2 });
        childrenByParent.set(ch.parent_ship_id, arr);
      });
    }
    // Mark variants with depth 1
    const variantsWithChildren = (variants || []).map(v => ({
      ...v,
      _depth: 1,
      _children: childrenByParent.get(v.id) || []
    }));
    const byParent = new Map();
    variantsWithChildren.forEach(v => { const a = byParent.get(v.parent_ship_id)||[]; a.push(v); byParent.set(v.parent_ship_id, a); });
    return { success: true, data: bases.map(b => ({ base: b, variants: byParent.get(b.id)||[] })) };
  } catch (e) { return { success: false, message: e.message }; }
};

/**
 * PRD-053B P1-1: Progressive loading - fetch only base ships (chassis)
 * This is the first tier of the 3-tier loading strategy
 */
export const listBaseShipsOnly = async () => {
  try {
    const { data: bases, error } = await supabase
      .from('ships')
      .select('id, name, manufacturer, category, sub_category, image_url, price, pledge_price, scu, in_game, flyable, variant_name, cig_store_url')
      .is('parent_ship_id', null)
      .order('manufacturer')
      .order('name');
    if (error) throw error;
    return { success: true, data: bases || [] };
  } catch (e) {
    return { success: false, message: e.message };
  }
};

/**
 * PRD-053B P1-1: Progressive loading - fetch variants for a specific chassis
 * This is the second tier of the 3-tier loading strategy
 * @param {string} chassisId - The UUID of the parent chassis
 */
export const listVariantsForChassis = async (chassisId) => {
  try {
    if (!chassisId) return { success: false, message: 'Chassis ID required' };
    const { data: variants, error } = await supabase
      .from('ships')
      .select('*')
      .eq('parent_ship_id', chassisId)
      .order('variant_name');
    if (error) throw error;
    // Also check how many children each variant has (for showing caret)
    const variantIds = (variants || []).map(v => v.id);
    let childCounts = new Map();
    if (variantIds.length > 0) {
      const { data: counts, error: countError } = await supabase
        .from('ships')
        .select('parent_ship_id')
        .in('parent_ship_id', variantIds);
      if (!countError && counts) {
        counts.forEach(c => {
          childCounts.set(c.parent_ship_id, (childCounts.get(c.parent_ship_id) || 0) + 1);
        });
      }
    }
    // Add child count to each variant
    const variantsWithCounts = (variants || []).map(v => ({
      ...v,
      _childCount: childCounts.get(v.id) || 0
    }));
    return { success: true, data: variantsWithCounts };
  } catch (e) {
    return { success: false, message: e.message };
  }
};

/**
 * PRD-053B P1-1: Progressive loading - fetch special editions for a specific variant
 * This is the third tier of the 3-tier loading strategy
 * @param {string} variantId - The UUID of the parent variant
 */
export const listEditionsForVariant = async (variantId) => {
  try {
    if (!variantId) return { success: false, message: 'Variant ID required' };
    const { data: editions, error } = await supabase
      .from('ships')
      .select('*')
      .eq('parent_ship_id', variantId)
      .order('variant_name');
    if (error) throw error;
    return { success: true, data: editions || [] };
  } catch (e) {
    return { success: false, message: e.message };
  }
};

/**
 * PRD-053B P1-1: Get variant count for multiple chassis (for showing carets)
 * @param {string[]} chassisIds - Array of chassis UUIDs
 */
export const getVariantCountsForChassis = async (chassisIds) => {
  try {
    if (!chassisIds || chassisIds.length === 0) return { success: true, data: new Map() };
    const { data, error } = await supabase
      .from('ships')
      .select('parent_ship_id')
      .in('parent_ship_id', chassisIds);
    if (error) throw error;
    const counts = new Map();
    (data || []).forEach(row => {
      counts.set(row.parent_ship_id, (counts.get(row.parent_ship_id) || 0) + 1);
    });
    return { success: true, data: counts };
  } catch (e) {
    return { success: false, message: e.message };
  }
};

/**
 * PRD-053B P2-1: Fetch entire ship hierarchy using recursive CTE
 * Replaces 3 sequential queries with a single database call
 *
 * IMPORTANT: This function marks ships with _depth and _isEdition fields
 * to ensure edition status is determined by hierarchy, not name keywords.
 *
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getShipHierarchyOptimized = async () => {
  try {
    const { data, error } = await supabase.rpc('get_ship_hierarchy');
    if (error) throw error;

    // Group by chassis (depth 0) with variants (depth 1) and editions (depth 2)
    const chassisMap = new Map();
    const variantMap = new Map();

    (data || []).forEach(ship => {
      if (ship.depth === 0) {
        // Chassis (base ship) - depth 0
        chassisMap.set(ship.id, { base: { ...ship, _depth: 0 }, variants: [] });
      } else if (ship.depth === 1) {
        // Variant - depth 1
        const variantWithChildren = { ...ship, _depth: 1, _children: [] };
        variantMap.set(ship.id, variantWithChildren);
        const parent = chassisMap.get(ship.parent_ship_id);
        if (parent) {
          parent.variants.push(variantWithChildren);
        }
      } else if (ship.depth === 2) {
        // Special edition (child of variant) - depth 2
        // Mark as edition - this is the SOURCE OF TRUTH for edition status
        const parentVariant = variantMap.get(ship.parent_ship_id);
        if (parentVariant) {
          parentVariant._children.push({ ...ship, _depth: 2, _isEdition: true });
        }
      }
    });

    return { success: true, data: Array.from(chassisMap.values()) };
  } catch (e) {
    return { success: false, message: e.message };
  }
};

export const listMyShips = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id;
    if (!uid) return { success: false, message: 'Not authenticated' };
    const { data, error } = await supabase
      .from('user_ships')
      .select('id, ship_id, status, acquisition_method, acquisition_date')
      .eq('user_id', uid)
      .order('acquisition_date', { ascending: false });
    if (error) throw error;
    return { success: true, data };
  } catch (e) { return { success: false, message: e.message }; }
};

export const addShipToMyInventory = async (shipId, method) => {
  try { 
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData?.user?.id;
    const { error } = await supabase
      .from('user_ships')
      .upsert(
        [{ user_id: uid, ship_id: shipId, acquisition_method: method }],
        { onConflict: 'user_id,ship_id' }
      );
    if (error) throw error;
    return { success: true };
  } catch (e) { return { success: false, message: e.message }; }
};

export const removeShipFromMyInventory = async (userShipId) => {
  try {
    const { error } = await supabase.from('user_ships').delete().eq('id', userShipId);
    if (error) throw error;
    return { success: true };
  } catch (e) { return { success: false, message: e.message }; }
};

// Price data functions for group share and commodity trading
export const savePriceDataFromGroupShare = async (priceData) => {
  /**
   * Save price data from group share transactions
   * @param {Array} priceData - Array of price data objects
   * Each object should have:
   * - commodity_id
   * - merchant_id
   * - price (per SCU)
   * - activity ('buy' for merchant buying from player)
   * - accepted_scus (array of SCU sizes)
   * - quantity (optional, for tracking volume)
   */
  try {
    if (!priceData || priceData.length === 0) return { success: true };

    // Filter out invalid entries
    const validData = priceData.filter(item => 
      item.commodity_id && 
      item.merchant_id && 
      item.price > 0
    );

    if (validData.length === 0) return { success: true };

    // Insert into price_history table
    const priceHistoryData = validData.map(item => ({
      commodity_id: item.commodity_id,
      merchant_id: item.merchant_id,
      price: item.price,
      activity: item.activity || 'buy',
      created_at: new Date().toISOString()
    }));

    const { error: priceHistoryError } = await supabase
      .from('price_history')
      .insert(priceHistoryData);

    if (priceHistoryError) {
      console.error('Error saving price history:', priceHistoryError);
      throw priceHistoryError;
    }

    // OPTIMIZATION: Fetch all existing merchant_commodity records in ONE query (prevents N+1)
    // Build pairs for lookup
    const merchantCommodityPairs = validData.map(item => ({
      merchant_id: item.merchant_id,
      commodity_id: item.commodity_id
    }));

    // Fetch all existing records at once using OR conditions
    const { data: existingRecords, error: fetchError } = await supabase
      .from('merchant_commodities')
      .select('*')
      .or(
        merchantCommodityPairs
          .map(pair => `and(merchant_id.eq.${pair.merchant_id},commodity_id.eq.${pair.commodity_id})`)
          .join(',')
      );

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error fetching merchant commodities:', fetchError);
      // Continue with upsert anyway
    }

    // Create lookup map for O(1) access
    const existingMap = new Map();
    if (existingRecords) {
      existingRecords.forEach(record => {
        const key = `${record.merchant_id}_${record.commodity_id}`;
        existingMap.set(key, record);
      });
    }

    // OPTIMIZATION: Prepare all updates/inserts for bulk operation
    const upsertData = validData.map(item => {
      const key = `${item.merchant_id}_${item.commodity_id}`;
      const existing = existingMap.get(key);

      const baseData = {
        merchant_id: item.merchant_id,
        commodity_id: item.commodity_id,
        activity: item.activity || 'buy',
        last_buy_price: item.price,
        accepted_scus: item.accepted_scus || []
      };

      if (existing) {
        // Update price history for existing record
        const priceHistory = existing.price_history || [];
        priceHistory.push({
          price: item.price,
          timestamp: new Date().toISOString(),
          quantity: item.quantity || null
        });

        // Keep only last 100 entries
        baseData.price_history = priceHistory.length > 100
          ? priceHistory.slice(-100)
          : priceHistory;
      } else {
        // New record - initialize price history
        baseData.price_history = [{
          price: item.price,
          timestamp: new Date().toISOString(),
          quantity: item.quantity || null
        }];
      }

      return baseData;
    });

    // OPTIMIZATION: Single bulk upsert instead of N individual queries
    const { error: upsertError } = await supabase
      .from('merchant_commodities')
      .upsert(upsertData, {
        onConflict: 'merchant_id,commodity_id'
      });

    if (upsertError) {
      console.error('Error upserting merchant commodities:', upsertError);
      throw upsertError;
    }

    // PRD-050: Also insert into commodity_prices for Charts page integration
    // This ensures price submissions from GroupShare appear in Charts
    try {
      // Fetch merchant location info to get location_id and location name
      const merchantIds = [...new Set(validData.map(item => item.merchant_id))];
      const { data: merchantLocations, error: locError } = await supabase
        .from('merchants')
        .select('id, name, location_id, game_locations!merchants_location_id_fkey(id, name)')
        .in('id', merchantIds);

      if (locError) {
        console.warn('Could not fetch merchant locations for Charts sync:', locError);
      } else if (merchantLocations) {
        // Create location lookup map
        const locationMap = new Map();
        merchantLocations.forEach(m => {
          if (m.location_id || m.game_locations) {
            locationMap.set(m.id, {
              location_id: m.location_id || m.game_locations?.id,
              location_name: m.game_locations?.name || m.name
            });
          }
        });

        // Prepare commodity_prices inserts
        const commodityPricesData = validData
          .filter(item => locationMap.has(item.merchant_id))
          .map(item => {
            const loc = locationMap.get(item.merchant_id);
            return {
              commodity_id: item.commodity_id,
              location_id: loc.location_id,
              location: loc.location_name,
              price: item.price,
              volume: item.quantity || 1,
              timestamp: new Date().toISOString()
            };
          });

        if (commodityPricesData.length > 0) {
          const { error: priceError } = await supabase
            .from('commodity_prices')
            .insert(commodityPricesData);

          if (priceError) {
            // Non-critical error - log but don't throw
            console.warn('Could not sync to commodity_prices for Charts:', priceError);
          } else {
            console.log(`Synced ${commodityPricesData.length} prices to Charts (commodity_prices)`);
          }
        }
      }
    } catch (chartsError) {
      // Non-critical - log but don't fail the main operation
      console.warn('Charts sync (commodity_prices) failed:', chartsError);
    }

    console.log(`Successfully saved ${validData.length} price data entries`);
    return { success: true, count: validData.length };
  } catch (error) {
    console.error('Error in savePriceDataFromGroupShare:', error);
    return { success: false, error: error.message };
  }
};

// Alias for commodity trading (uses same function as group share)
export const submitCommodityPrices = savePriceDataFromGroupShare;

// Group Share Live Sharing functions
export const createGroupShareLink = async (shareData, isPremiumUser = false) => {
  try {
    const { data: user } = await supabase.auth.getUser();
    const userId = user?.user?.id;
    
    // Check rate limit for free users
    if (!isPremiumUser && userId) {
      // Check if user has already created a share today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: existingShares, error: checkError } = await supabase
        .from('group_shares')
        .select('id')
        .eq('user_id', userId)
        .gte('created_at', today.toISOString())
        .limit(1);
      
      if (!checkError && existingShares && existingShares.length > 0) {
        console.log('Free user daily limit reached');
        return {
          success: false,
          shareId: null,
          error: 'daily_limit',
          message: "You've used your 1 free share for today. Upgrade to Ledger for unlimited shares."
        };
      }
    }
    
    // Create a unique share ID
    const shareId = `share_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Set expiration to 24 hours for all users
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    
    // Save to group_shares table
    const { error } = await supabase
      .from('group_shares')
      .insert([{
        id: shareId,
        user_id: userId || null,
        share_data: shareData,
        created_at: new Date().toISOString(),
        expires_at: expiresAt,
        is_premium: isPremiumUser
      }]);

    if (error) {
      console.error('Error creating share link:', error);
      // If table doesn't exist, return a client-side only share
      return { 
        success: false, 
        shareId: null,
        fallbackData: shareData 
      };
    }
    
    return { 
      success: true, 
      shareId: shareId,
      expiresAt: expiresAt
    };
  } catch (error) {
    console.error('Error in createGroupShareLink:', error);
    return { 
      success: false, 
      shareId: null,
      fallbackData: shareData 
    };
  }
};

export const updateGroupShareLink = async (shareId, shareData) => {
  try {
    // First check if the record exists
    const { data: existing, error: checkError } = await supabase
      .from('group_shares')
      .select('id')
      .eq('id', shareId)
      .single();
    
    if (checkError || !existing) {
      // If record doesn't exist, try to create it
      const { error: insertError } = await supabase
        .from('group_shares')
        .insert([{
          id: shareId,
          share_data: shareData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }]);
      
      if (insertError) {
        console.error('Error creating share record:', insertError);
        return { success: false, error: insertError };
      }
      return { success: true };
    }
    
    // Update existing record
    const { error } = await supabase
      .from('group_shares')
      .update({
        share_data: shareData,
        updated_at: new Date().toISOString()
      })
      .eq('id', shareId);
    
    if (error) {
      console.error('Error updating share link:', error);
      return { success: false, error };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error in updateGroupShareLink:', error);
    return { success: false, error };
  }
};

export const fetchGroupShareData = async (shareId) => {
  try {
    console.log('Fetching group share data for ID:', shareId);
    
    const { data, error } = await supabase
      .from('group_shares')
      .select('*')
      .eq('id', shareId)
      .single();
    
    if (error) {
      console.error('Error fetching share data:', error);
      // Check if it's a "table doesn't exist" error
      if (error.message?.includes('relation') && error.message?.includes('does not exist')) {
        console.log('Group shares table does not exist. Please run the migration.');
        return null;
      }
      return null;
    }
    
    if (!data) {
      console.log('No data found for share ID:', shareId);
      return null;
    }
    
    // Check if expired
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      console.log('Share link has expired at:', data.expires_at);
      // Return expired indicator
      return { expired: true, expiresAt: data.expires_at };
    }
    
    console.log('Successfully fetched share data');
    return data.share_data;
  } catch (error) {
    console.error('Error in fetchGroupShareData:', error);
    return null;
  }
};
